/**
 * Snapshot migration (issue #17; CONTEXT.md, "Snapshot migration"): what happens to a stored
 * Character when the app adopts a new Snapshot. Three passes: the redirect pass and the
 * resolution pass (./snapshot.ts, pure), then the re-evaluation pass here, which settles the
 * Character (grants reconciled, progression opened, sheet derived) and reopens whatever the
 * guide no longer finds structurally legal (../guide/changes.ts). Each migration is recorded on
 * the Character with both Snapshot tags and shown once as the migration report.
 *
 * The runner covers every stored Character at launch and refuses while a Session is open; the
 * Character page migrates a stale Character on open; an import from an older Snapshot goes
 * through the same passes. The update-apply hook of #3 calls `runSnapshotMigration` with the
 * previous index so the report can name retired Entries.
 */
import type { IndexRecord } from "../../shared/snapshot-format";
import { now } from "../character/create";
import { materialiseGrants, saveImported } from "../character/index";
import { appendEntry } from "../session/log";
import type { Character, MigrationReopened, Reopened, SnapshotMigration } from "../character/model";
import { findChoice } from "../character/model";
import { getCharacter, listCharacters, putCharacter } from "../character/store";
import { parseEnvelope } from "../character/transfer";
import { loadEngineCatalogue } from "../data/config";
import { loadRedirects } from "../data/entries";
import { loadManifest } from "../data/manifest";
import { reopenIllegal, type Settle } from "../guide/changes";
import { buildGuideContext } from "../guide/context";
import type { GuideContext } from "../guide/context";
import { grantedSelections, isUuid, redirectAndResolve, type MigrationTarget } from "./snapshot";

export { followRedirects, isUuid, redirectAndResolve, MAX_REDIRECT_HOPS } from "./snapshot";
export type { MigrationTarget, ResolvedCharacter } from "./snapshot";
export type { Settle };

export interface MigrationOutcome {
  character: Character;
  /** The record added to the Character; null when it already stood under the target Snapshot. */
  report: SnapshotMigration | null;
}

/** The app's settle: reconcile grants, loading Entries from the Snapshot, and build the guide's context. */
export async function appSettle(character: Character): Promise<GuideContext> {
  const m = await materialiseGrants(character);
  return buildGuideContext(m.character, m.entries, await loadEngineCatalogue());
}

/**
 * The target for the Snapshot this build ships. `previousIndex` is the index of the Snapshot
 * being replaced, when the caller still has it (#3's update hook), so retired Entries keep their names in the report.
 */
export async function loadMigrationTarget(previousIndex?: readonly IndexRecord[]): Promise<MigrationTarget> {
  const [manifest, redirects, catalogue] = await Promise.all([loadManifest(), loadRedirects(), loadEngineCatalogue()]);
  const old = previousIndex ? new Map(previousIndex.map((r) => [r.u, r.n])) : null;
  return {
    to: manifest.snapshot.release,
    redirects,
    has: (uuid) => catalogue.byUuid(uuid) !== undefined,
    nameOf: (uuid) => catalogue.byUuid(uuid)?.n ?? null,
    oldNameOf: (uuid) => old?.get(uuid) ?? null,
  };
}

/**
 * Migrate one Character to the target Snapshot: the redirect and resolution passes, a settle,
 * the re-evaluation pass, then the record. A Character already under the target is returned as
 * it is (same reference), so running the migration twice changes nothing.
 */
export async function migrateToSnapshot(input: Character, target: MigrationTarget, settle: Settle, at = now()): Promise<MigrationOutcome> {
  if (input.snapshotTag === target.to) return { character: input, report: null };
  const from = input.snapshotTag;
  const first = redirectAndResolve(input, target);

  // The settle lets the reconciler replace or remove granted Selections whose granter changed or went missing.
  const before = grantedSelections(first.character);
  const settled = await settle(first.character);
  const removed = [...first.removed];
  const after = grantedSelections(settled.character);
  for (const [key, was] of before) {
    const is = after.get(key);
    const name = target.nameOf(was.uuid) ?? target.oldNameOf(was.uuid);
    if (!is) removed.push({ kind: "grant", uuid: was.uuid, name, detail: `Level ${was.level}, ${key}` });
    else if (is.uuid !== was.uuid) removed.push({ kind: "grant", uuid: was.uuid, name, detail: `Level ${was.level}, ${key}: replaced by ${target.nameOf(is.uuid) ?? is.uuid}` });
  }

  // The re-evaluation pass: what the guide no longer finds legal reopens with a warning too.
  const re = await reopenIllegal(settled, settle);
  let character = re.ctx.character;
  const reopened: MigrationReopened[] = [...first.reopened];
  if (re.reopened.length > 0) {
    character = structuredClone(character);
    for (const r of re.reopened) {
      const held = findChoice(settled.character, r.key);
      const uuid = r.flag === null ? (held?.selection?.uuid ?? null) : isUuid(held?.answers?.[r.flag]) ? (held!.answers![r.flag] as string) : null;
      const record: Reopened = { from, to: target.to, flag: r.flag, uuid, name: uuid ? (settled.entries.get(uuid)?.name ?? target.nameOf(uuid)) : null, reason: "illegal", detail: r.reason };
      const choice = findChoice(character, r.key);
      if (choice) choice.reopened = [...(choice.reopened ?? []), record];
      reopened.push({ ...record, key: r.key, level: r.level, label: r.label });
    }
  }

  const report: SnapshotMigration = { from, to: target.to, at, redirected: first.redirected, reopened, removed, seen: false };
  // Session items the new Snapshot lacks were dropped (an imported file with a Session open): the Roll Log says so (#20).
  const sessionRemoved = removed.filter((r) => r.kind === "condition" || r.kind === "effect" || r.kind === "consumable");
  const rollLog = character.session && sessionRemoved.length > 0
    ? appendEntry(character.rollLog, {
        at,
        round: character.session.round,
        kind: "note",
        type: "note",
        summary: `Snapshot migration ${from} → ${target.to} removed from the Session: ${sessionRemoved.map((r) => `${r.name ?? r.uuid} (${r.kind})`).join(", ")}`,
        events: sessionRemoved.map((r) => ({ kind: r.kind === "effect" ? "effect" as const : r.kind === "consumable" ? "consumable" as const : "condition" as const, text: `${r.name ?? r.uuid}${r.detail ? ` · ${r.detail}` : ""}` })),
        action: null,
        inverse: null,
        reverses: null,
      }).log
    : character.rollLog;
  return { character: { ...character, snapshotTag: target.to, migrations: [...character.migrations, report], rollLog }, report };
}

export interface EnsuredCharacter extends MigrationOutcome {
  /** Why a stale Character was left as it is: a Session is open on it. */
  refused: string | null;
}

/**
 * Migrate a loaded Character when it is stale and save it; leave it alone under an open Session
 * (docs/design.md, "Delivery": never under an open Session). The Character page calls this on open.
 */
export async function ensureMigrated(character: Character, target: MigrationTarget, settle: Settle): Promise<EnsuredCharacter> {
  if (character.snapshotTag === target.to) return { character, report: null, refused: null };
  if (character.session) return { character, report: null, refused: `The Snapshot changed from ${character.snapshotTag} to ${target.to} while a Session was open on ${character.name}; close the Session to migrate it.` };
  const outcome = await migrateToSnapshot(character, target, settle);
  return { ...outcome, character: await putCharacter(outcome.character), refused: null };
}

export interface MigrationRun {
  to: string;
  /** Why nothing ran: a Session is open on a Character. */
  refused: string | null;
  migrated: { id: string; name: string; report: SnapshotMigration }[];
  /** Characters the migration could not read or settle; they migrate when opened. */
  failed: { id: string; name: string; error: string }[];
}

/**
 * The runner: migrate every stored Character that stands under another Snapshot. Refuses as a
 * whole while any Character has a Session open. Nothing is touched when every Character is current.
 */
export async function runSnapshotMigration(target?: MigrationTarget, settle: Settle = appSettle): Promise<MigrationRun> {
  const t = target ?? (await loadMigrationTarget());
  const summaries = await listCharacters();
  const stale = summaries.filter((s) => s.snapshotTag !== t.to);
  const run: MigrationRun = { to: t.to, refused: null, migrated: [], failed: [] };
  if (stale.length === 0) return run;
  const inPlay = summaries.filter((s) => s.sessionOpen);
  if (inPlay.length > 0) {
    run.refused = `${inPlay.map((s) => s.name).join(", ")} ${inPlay.length === 1 ? "has" : "have"} a Session open; the Snapshot migration to ${t.to} waits until every Session is closed.`;
    return run;
  }
  for (const summary of stale) {
    try {
      const character = await getCharacter(summary.id);
      if (!character) continue;
      const outcome = await ensureMigrated(character, t, settle);
      if (outcome.refused) {
        run.refused = outcome.refused;
        return run;
      }
      if (outcome.report) run.migrated.push({ id: outcome.character.id, name: outcome.character.name, report: outcome.report });
    } catch (e) {
      run.failed.push({ id: summary.id, name: summary.name, error: (e as Error).message });
    }
  }
  return run;
}

export interface ImportOutcome {
  character: Character;
  /** The file's id was taken by another Character, so the import became a copy with a new id. */
  copied: boolean;
  /** The migration record when the file came from an older Snapshot; null otherwise. */
  report: SnapshotMigration | null;
}

/**
 * Import a Character from file text (#4), migrating it first when the file was exported under
 * another Snapshot. Nothing stored is ever overwritten.
 */
export async function importCharacter(text: string, target?: MigrationTarget, settle: Settle = appSettle): Promise<ImportOutcome> {
  const env = parseEnvelope(text);
  const t = target ?? (await loadMigrationTarget());
  const outcome = await migrateToSnapshot(env.character, t, settle);
  const saved = await saveImported(outcome.character);
  return { ...saved, report: outcome.report };
}

/** "3 Selections redirected, 1 reopened, 2 removed": the report in a line. */
export function describeMigration(report: SnapshotMigration): string {
  const parts = [`${report.redirected} ${report.redirected === 1 ? "UUID" : "UUIDs"} redirected`];
  if (report.reopened.length > 0) parts.push(`${report.reopened.length} reopened`);
  if (report.removed.length > 0) parts.push(`${report.removed.length} removed`);
  return `${report.from} to ${report.to}: ${parts.join(", ")}`;
}

/** The records of a Character not yet shown as a report. */
export function unseenMigrations(character: Character): SnapshotMigration[] {
  return character.migrations.filter((m) => !m.seen);
}

/** Mark every record seen (the report was dismissed). Returns the input when nothing changes. */
export function markMigrationsSeen(character: Character): Character {
  if (!character.migrations.some((m) => !m.seen)) return character;
  return { ...character, migrations: character.migrations.map((m) => (m.seen ? m : { ...m, seen: true })) };
}

/** The guide item id a reopened record points at: the Choice key, plus the flag for an Answer. */
export function reopenedItemId(r: MigrationReopened): string {
  return r.flag === null ? r.key : `${r.key}#${r.flag}`;
}
